import React, { useCallback, useEffect, useState } from "react";
import ModalComponent from "../../../components/modals";
import LaddaButton from "../../../components/buttons/laddaButton";
import { useForm } from "react-hook-form";
import { Grid } from "@mui/material";
import SimpleSelect from "../../../components/selects/simpleSelect";
import ShowTable from "../../../components/tables/showTable";
import {
  getBiomedicalLocationEquipmentSummary,
  postDeleteOrMigrateBiomedicalLocation
} from "../../../services/apiService";
import ToastNotification from "../../../components/toastNotification";

const EQUIPMENT_TABLE_COLUMNS = [
  { field: "id", header: "ID", width: "72px" },
  { field: "name", header: "Nombre" },
  { field: "brand_name", header: "Marca" },
  { field: "model", header: "Modelo" },
  { field: "serie_name", header: "Serie" }
];

const DeleteLocationModal = ({
  open,
  close,
  locationRow,
  allRows,
  userId,
  onDone
}) => {
  const [showToast, setShowToast] = useState(false);
  const [toastConfig, setToastConfig] = useState({
    severity: "warning",
    summary: "",
    detail: ""
  });
  const [relatedEquipment, setRelatedEquipment] = useState([]);
  const [summaryLoading, setSummaryLoading] = useState(false);
  const [summaryError, setSummaryError] = useState(null);
  const [loading, setLoading] = useState(false);

  const { control, reset, handleSubmit, watch } = useForm({
    defaultValues: { migrateToLocationId: "" }
  });

  const migrateToWatch = watch("migrateToLocationId");

  const migrateOptions = (allRows || [])
    .filter((r) => Number(r.value) !== Number(locationRow?.value))
    .map((r) => ({ name: r.name, value: String(r.value) }));

  const hasMigrateTargets = migrateOptions.length > 0;
  const relatedCount = relatedEquipment.length;
  const hasRelatedEquipment = relatedCount > 0;

  const pushToast = useCallback((severity, summary, detail) => {
    setToastConfig({ severity, summary, detail });
    setShowToast(true);
  }, []);

  const loadEquipmentSummary = useCallback(async () => {
    const lid = locationRow?.value;
    if (lid == null || lid === "" || !userId) {
      setRelatedEquipment([]);
      setSummaryError(null);
      return;
    }
    setSummaryLoading(true);
    setSummaryError(null);
    try {
      const { data } = await getBiomedicalLocationEquipmentSummary(lid, userId);
      setRelatedEquipment(Array.isArray(data?.equipment) ? data.equipment : []);
    } catch (err) {
      const msg =
        err?.response?.data?.error ||
        "No se pudo cargar el listado de equipos para esta ubicación.";
      setSummaryError(msg);
      setRelatedEquipment([]);
      pushToast("error", "Error", msg);
    } finally {
      setSummaryLoading(false);
    }
  }, [locationRow?.value, userId, pushToast]);

  useEffect(() => {
    if (open && locationRow?.value != null && userId) {
      reset({ migrateToLocationId: "" });
      loadEquipmentSummary();
    } else if (!open) {
      setRelatedEquipment([]);
      setSummaryError(null);
      reset({ migrateToLocationId: "" });
    }
  }, [open, locationRow?.value, userId, reset, loadEquipmentSummary]);

  const handleClose = () => {
    setRelatedEquipment([]);
    setSummaryError(null);
    reset();
    close();
  };

  const submitDelete = async (formValues) => {
    if (!locationRow?.value || !userId) return;

    if (summaryLoading || summaryError) {
      pushToast(
        "warn",
        "Espere",
        "Espere a que termine de cargar la información de la ubicación."
      );
      return;
    }

    if (hasRelatedEquipment) {
      if (!hasMigrateTargets) {
        pushToast(
          "error",
          "Sin ubicación destino",
          `Esta ubicación tiene ${relatedCount} equipo(s) biomédico(s) asignado(s), pero no hay otra ubicación en el catálogo. Cree una ubicación nueva y vuelva a intentar.`
        );
        return;
      }
      const rawDest = formValues.migrateToLocationId;
      const destStr = rawDest != null ? String(rawDest).trim() : "";
      if (!destStr) {
        pushToast(
          "warn",
          "Ubicación destino requerida",
          `Debe elegir la nueva ubicación para los ${relatedCount} equipo(s) antes de eliminar.`
        );
        return;
      }
    }

    setLoading(true);
    try {
      const migrateTo =
        hasRelatedEquipment &&
        formValues.migrateToLocationId &&
        String(formValues.migrateToLocationId).trim() !== ""
          ? Number(formValues.migrateToLocationId)
          : undefined;

      await postDeleteOrMigrateBiomedicalLocation({
        userId,
        id: locationRow.value,
        migrateToLocationId: migrateTo
      });
      onDone?.();
      handleClose();
    } catch (err) {
      const status = err?.response?.status;
      const data = err?.response?.data;
      if (status === 409 && data?.equipmentCount != null) {
        await loadEquipmentSummary();
        pushToast(
          "warning",
          "Migración requerida",
          `Hay ${data.equipmentCount} equipo(s) en esta ubicación. Elija una ubicación destino y confirme de nuevo.`
        );
      } else {
        const msg =
          typeof data?.error === "string"
            ? data.error
            : "No se pudo eliminar la ubicación.";
        pushToast("error", "Error", msg);
      }
    } finally {
      setLoading(false);
    }
  };

  const tableData = relatedEquipment.map((row) => ({
    ...row,
    brand_name: row.brand_name ?? "—",
    serie_name: row.serie_name ?? "—",
    model: row.model ?? "—"
  }));

  const submitDisabled =
    loading ||
    summaryLoading ||
    Boolean(summaryError) ||
    (hasRelatedEquipment &&
      (!hasMigrateTargets ||
        !migrateToWatch ||
        String(migrateToWatch).trim() === ""));

  return (
    <>
      <ModalComponent
        open={open}
        handleClose={handleClose}
        title="Eliminar ubicación"
        maxWidth="800px"
        actions={<div className="HContainer j-center w-complete"></div>}
      >
        <div className="HContainer">
          <form
            className="VContainer align-center m-20"
            onSubmit={handleSubmit(submitDelete)}
            style={{ width: "100%", maxWidth: "760px" }}
          >
            <p className="mb-15" style={{ maxWidth: "640px" }}>
              ¿Eliminar la ubicación <strong>{locationRow?.name}</strong>?
            </p>

            {summaryLoading && (
              <p className="mb-15">Cargando equipos vinculados a esta ubicación…</p>
            )}

            {summaryError && !summaryLoading && (
              <p className="mb-15" style={{ color: "#c62828" }}>
                {summaryError}
              </p>
            )}

            {!summaryLoading && !summaryError && !hasRelatedEquipment && (
              <p className="mb-15" style={{ maxWidth: "640px", color: "#2e7d32" }}>
                No hay equipos biomédicos con esta ubicación. Puede eliminarla de
                forma directa.
              </p>
            )}

            {!summaryLoading && !summaryError && hasRelatedEquipment && (
              <>
                <div
                  className="mb-15"
                  style={{
                    maxWidth: "640px",
                    padding: "12px 14px",
                    background: "#fff3e0",
                    borderRadius: "8px",
                    border: "1px solid #ffb74d"
                  }}
                  role="alert"
                >
                  <strong>No se puede eliminar la ubicación todavía.</strong> Tiene{" "}
                  <strong>{relatedCount}</strong> equipo(s) biomédico(s) con esta
                  ubicación asignada. Para eliminarla debe escoger una{" "}
                  <strong>nueva ubicación</strong> para esos equipos; se migrarán y
                  luego se eliminará la ubicación actual.
                </div>

                <div className="mb-15 w-complete" style={{ maxHeight: "320px" }}>
                  <p className="create-label mb-8">Resumen de equipos a migrar</p>
                  <ShowTable
                    data={tableData}
                    columns={EQUIPMENT_TABLE_COLUMNS}
                    pagination
                    rows={10}
                    scrollable
                    scrollHeight="240px"
                    stripedRows
                    compact
                    emptyMessage="Sin equipos en esta ubicación"
                  />
                </div>
              </>
            )}

            {hasRelatedEquipment && !hasMigrateTargets && !summaryLoading && (
              <p className="mb-15" style={{ maxWidth: "640px", color: "#c62828" }}>
                No hay otra ubicación disponible en el catálogo. Cree una ubicación
                nueva y vuelva a abrir este diálogo.
              </p>
            )}

            {hasRelatedEquipment && hasMigrateTargets && !summaryLoading && (
              <Grid container spacing={2} className="w-complete">
                <Grid item xs={12}>
                  <p className="create-label">
                    Nueva ubicación para los {relatedCount} equipo(s)
                  </p>
                  <SimpleSelect
                    name="migrateToLocationId"
                    control={control}
                    label=""
                    options={migrateOptions}
                    margin="0"
                  />
                </Grid>
              </Grid>
            )}

            <div className="HContainer j-center mt-20">
              <LaddaButton
                type="submit"
                variant="contained"
                disabled={submitDisabled}
              >
                {hasRelatedEquipment
                  ? "Migrar equipos y eliminar ubicación"
                  : "Eliminar"}
              </LaddaButton>
            </div>
          </form>
        </div>
      </ModalComponent>
      <ToastNotification
        showToast={showToast}
        setShowToast={setShowToast}
        severity={toastConfig.severity}
        summary={toastConfig.summary}
        detail={toastConfig.detail}
      />
    </>
  );
};

export default DeleteLocationModal;
